import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { CalendarDays, Cloud, Drone, Eye, Hourglass, Info, type LucideIcon, Plane, RotateCw, SlidersHorizontal, Sunrise, Sunset, Thermometer, Wind } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { api } from "../api/client";
import type { NamedPlace, WeatherCustom, WeatherOptions, ZoneLetter } from "../api/types";
import { flashAttention } from "../components/attention";
import { Callout } from "../components/Callout";
import { EmptyState, Spinner } from "../components/EmptyState";
import { Motion, Presence, useLastDefined } from "../components/presence";
import { Badge, Button, Field, inputClass, SectionLabel, Segmented } from "../components/ui";
import { Waiting } from "../components/Waiting";
import { smallDronesEffect } from "../drones";
import { locale, t } from "../i18n/i18n";
import { CLIMATES } from "../map/zones/climates";
import { useZoneList } from "../map/zones/useZones";
import { useUi } from "../state/ui";
import {
  admittedTemperature,
  assessmentLabel,
  assessmentTone,
  ceilingText,
  choiceLabel,
  climateLabel,
  CUSTOM,
  customOf,
  darkNightLabel,
  degrees,
  flightEffect,
  pictogramLabel,
  placeContext,
  placeWeatherKey,
  previewLine,
  readjustOnContext,
  readjustText,
  scenarioLabel,
  SEASONAL,
  seasonalSky,
  skyLabel,
  span,
  stillPossible,
  temperatureProblem,
  temperatureText,
  visibilityText,
  type WeatherContext,
  weatherGap,
  weatherGapText,
  weatherKey,
  weatherOptionsKey,
  weatherState,
  WIND_FROM_SKY,
  windLabel,
  windsFor,
  windText,
  withSky,
} from "../weather";
import { MoonPhase, pictogramIcon, windIcon } from "../weatherIcons";
import { onRadioGroupKeyDown, rovingTabIndex } from "./radioGroupKeys";

type Mode = "seasonal" | "custom";

/** Climat du terrain pas encore définitif, dit par le parent qui sait où en est le lieu : la phrase qui le dit, et
 *  l'action attendue, désignée par un appui sur « Choisie » (`onBlocked`). Lieu à choisir, ou terrain libre à placer :
 *  rien n'est interrogé (ni conditions ni aperçu) ; terrain libre en préparation (`provisional`) : l'aperçu de la
 *  saison reste montré, sur un climat provisoire. */
export interface WeatherPending {
  hint: string;
  onBlocked?: () => void;
  provisional?: boolean;
}

/** Phrase d'un terrain libre en préparation : son climat (et donc la météo proposée) n'est pas encore définitif. */
export function provisionalHint(): string {
  return t("Climat provisoire : il sera confirmé à la fin de la préparation du terrain. La météo choisie se règle dès que le terrain est prêt.");
}

/** Phrase d'un climat estimé d'après les coordonnées : celui du lieu nommé d'un nouveau terrain, ou celui d'un terrain
 *  de la licence encore en préparation. « Choisie » est offerte sur ce climat, confirmé quand le terrain sera prêt. */
export function approximateHint(place: Pick<NamedPlace, "name"> | null | undefined, climate: string): string {
  const label = estimatedClimate(climate);
  return place
    ? t("Climat estimé d'après « {lieu} » : {climat}. Il sera confirmé quand le terrain sera prêt.", { lieu: place.name, climat: label })
    : t("Climat estimé : {climat}. Il sera confirmé à la fin de la préparation du terrain.", { climat: label });
}

/** Nom court d'un climat dans une phrase (« semi-continental ») : celui du réglage « Climat », en minuscules. */
export function estimatedClimate(climate: string): string {
  const known = CLIMATES.find((c) => c.key === climate);
  return known ? t(known.label).toLocaleLowerCase(locale()) : climate;
}

/** Options du réglage pour une carte, une date, un fuseau et la météo envoyée (`POST /maps/{slug}/weather`), en cache
 *  par terrain (état et climat de la zone compris) ; `waiting` : lieu inconnu, rien d'interrogé. Partagées par le
 *  réglage et le résumé de l'étape Terrain (une seule requête). `place` : lieu nommé d'un nouveau terrain pas encore
 *  placé — il remplace la carte (celle d'avant, que le terrain remplacera, ne compte pas) et les options viennent de
 *  `POST /places/weather`, sur le climat estimé d'après ses coordonnées. */
export function useWeatherOptions({
  mapSlug,
  place = null,
  startDtg,
  timeZone,
  scenario,
  custom,
  waiting = false,
}: {
  mapSlug: string | null;
  place?: NamedPlace | null;
  startDtg: string | null;
  timeZone: ZoneLetter | null;
  scenario: string | null;
  custom: WeatherCustom | null | undefined;
  waiting?: boolean;
}) {
  const key = weatherKey({ weather_scenario: scenario, weather_custom: custom ?? null });
  const { data: zoneList } = useZoneList();
  const slug = place ? null : mapSlug;
  const zone = slug ? (zoneList?.find((z) => z.slug === slug) ?? null) : null;
  const body = {
    start_dtg: startDtg,
    time_zone: timeZone,
    weather_scenario: scenario,
    weather_custom: scenario === CUSTOM ? (custom ?? null) : null,
  };
  const query = useQuery<WeatherOptions>({
    queryKey: place ? placeWeatherKey(place, startDtg, timeZone, key) : weatherOptionsKey(slug, zone, startDtg, timeZone, key),
    queryFn: () => (place ? api.placeWeather({ lon: place.lon, lat: place.lat, ...body }) : api.weatherOptions(slug!, body)),
    enabled: Boolean(place || slug) && !waiting,
    // Données d'avant gardées le temps du chargement, sur la même carte (ou au même lieu) seulement : un autre lieu ne
    // propose pas les mêmes conditions.
    placeholderData: (previous, previousQuery) => {
      const before = previousQuery?.queryKey;
      if (!before) return undefined;
      if (place) return before[1] === "place" && before[2] === place.lon && before[3] === place.lat ? previous : undefined;
      return before[1] === slug ? previous : undefined;
    },
  });
  return { query, key, options: (place || slug) && !waiting ? query.data : undefined };
}

/** Réglage « Météo », le même aux Données initiales et à l'étape Terrain de la vue Manœuvre (chantier de cohérence
 *  météo, 30/10/2026). « Saisonnière » (défaut) : la météo du climat du terrain à la date de début. « Choisie » : ciel
 *  et précipitations, puis vent, en tuiles à pictogramme, et température ; seules les conditions possibles pour le
 *  climat et la saison du terrain sont proposées. « Choisie » n'est offerte que sur un climat définitif (un camp, ou
 *  un terrain libre prêt) ; avant, elle est estompée, une phrase dit pourquoi et un appui désigne l'action attendue.
 *  Passer sur « Choisie » n'écrit rien : la saison reste appliquée tant qu'aucun ciel n'est choisi.
 *
 *  Le serveur est garant de la météo appliquée : un choix devenu inapplicable (autre climat, fin de préparation) est
 *  montré tel qu'il est enregistré, avec l'écart et deux actions ; rien n'est écrit sans geste, jamais à l'ouverture.
 *  Seul un brouillon (`adjust`, Données initiales) réajuste son choix, et seulement après un changement de lieu, de
 *  date ou de fuseau fait dans ce brouillon, en disant la vraie cause. Un scénario du catalogue (dossier importé) se
 *  présente pour ce qu'il est.
 *
 *  Dessous, l'aperçu calculé par le serveur pour la météo appliquée : pictogramme, appréciation, température,
 *  visibilité, plafond et vent, effets sur la manœuvre (délais majorés, moyens aériens contraints) et éphéméride du
 *  jour de l'heure H (soleil, crépuscule nautique, phase de la Lune). */
export function WeatherField({
  mapSlug,
  startDtg,
  timeZone,
  scenario,
  custom,
  onChange,
  conflict,
  pending = null,
  adjust = false,
  locked,
  readOnly,
  attention = "dossier-weather",
  label,
  place = null,
  onGap,
}: {
  mapSlug: string | null;
  /** Lieu nommé du nouveau terrain (Données initiales) : la météo se propose sur son climat estimé. */
  place?: NamedPlace | null;
  /** Écart de la météo envoyée sur le climat du lieu (verdict sûr), remonté au parent (étape « Date et météo »). */
  onGap?: (gap: boolean) => void;
  startDtg: string | null;
  timeZone: ZoneLetter | null;
  scenario: string | null;
  custom: WeatherCustom | null | undefined;
  /** `how.deferred` : choix fait aux flèches du clavier (la sélection suit le focus) ; un parent qui écrit le
   *  dossier à chaque choix peut attendre le dernier. */
  onChange: (scenario: string, custom: WeatherCustom | null, how?: { deferred?: boolean }) => void;
  conflict?: ReactNode;
  pending?: WeatherPending | null;
  /** Brouillon (Données initiales) : réajusté après un changement de lieu, de date ou de fuseau fait ici. */
  adjust?: boolean;
  /** Écriture impossible (brouillon modifié, lecture seule) : chaque commande désigne l'action attendue. */
  locked?: () => void;
  /** Compte en lecture seule : les actions de l'écart désignent les droits. */
  readOnly?: () => void;
  /** Cible `data-attention` du réglage. */
  attention?: string;
  /** Intitulé du réglage (« Météo » par défaut ; « Météo de l'exercice » sous le climat d'un terrain partagé). */
  label?: string;
}) {
  const params = { weather_scenario: scenario, weather_custom: custom ?? null };
  const state = weatherState(params);
  const choice = customOf(params);
  // Lieu inconnu (à choisir, ou terrain à placer) : rien à interroger.
  const waiting = Boolean(pending && !pending.provisional);
  const { query, key, options } = useWeatherOptions({ mapSlug, place, startDtg, timeZone, scenario, custom, waiting });
  // Verdict sûr : réponse à la météo envoyée, ni données d'avant ni relecture en cours.
  const fresh = Boolean(options) && !query.isPlaceholderData && !query.isFetching;
  // Climat estimé d'après les coordonnées (lieu nommé, terrain en préparation sur un serveur qui l'estime) : « Choisie »
  // est offerte, sur ce climat ; un climat provisoire sans estimation (serveur antérieur) la fait attendre.
  const approximate = Boolean(options?.approximate);
  const provisional = Boolean(pending?.provisional || options?.provisional) && !approximate;
  // Lieu du verdict : la carte, ou le lieu nommé (réajustement « à cet endroit »).
  const context = place ? placeContext(place) : mapSlug;
  // Identifiant que le serveur ne connaît pas (texte libre) : lu comme la saisonnière.
  const unknown = state === "imported" && Boolean(options) && !query.isPlaceholderData && !options!.scenario;
  const imported = state === "imported" && !unknown;
  // « Choisie » demandée (ou « Choisir une autre météo ») : tuiles montrées, rien d'écrit avant le choix d'un ciel ;
  // elles restent pendant la composition.
  const [composing, setComposing] = useState(false);
  // Changement de ciel fait ici : vent ou température repris (dit sous les ciels).
  const [adjusted, setAdjusted] = useState<("wind" | "temperature")[]>([]);
  // Réajustement après un changement de lieu ou de date fait dans le brouillon, et sa cause.
  const [note, setNote] = useState<{ text: string; tone: "warn" | "info" } | null>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  // Préalable manquant de « Choisie » : lieu à choisir, terrain à placer ou à préparer, options en échec.
  const blocked = waiting || provisional || (!options && query.isError);
  const mode: Mode = state === "custom" || imported || (composing && !blocked) ? "custom" : "seasonal";
  const gap = options && !query.isPlaceholderData ? weatherGap(options, key) : null;
  const hasGap = Boolean(gap) && fresh;
  useEffect(() => {
    onGap?.(hasGap);
  }, [hasGap, onGap]);
  // Météo remise sur la saisonnière d'ailleurs (« Annuler les modifications », report, réajustement) : la
  // composition en cours n'a plus lieu d'être.
  const lastScenario = useRef(scenario);
  useEffect(() => {
    if (lastScenario.current !== scenario && (!scenario || scenario === SEASONAL)) setComposing(false);
    lastScenario.current = scenario;
  }, [scenario]);

  // Contexte du verdict (carte et mois de l'exercice) : le premier, vu à l'ouverture, ne réajuste jamais rien ; un
  // autre, venu d'un changement de lieu, de date ou de fuseau fait dans ce brouillon, réajuste un choix devenu
  // inapplicable comme les ordres l'appliquent, en disant la vraie cause. Un événement du terrain (climat changé,
  // fin de préparation) ne change pas ce contexte : l'écart est seulement signalé.
  const seen = useRef<WeatherContext | null>(null);
  // Dernière composition quittée (« Saisonnière », réajustement) : « Reprendre » la rend au retour sur « Choisie ».
  const memory = useRef<WeatherCustom | null>(null);
  useEffect(() => {
    if (!options || !fresh || !context) return;
    const verdict = { map: context, month: options.month };
    const before = seen.current;
    seen.current = verdict;
    if (!before || (before.map === verdict.map && before.month === verdict.month)) return;
    // Autre lieu ou autre mois : ce qui avait été dit pour le précédent n'a plus lieu d'être.
    setNote(null);
    setAdjusted([]);
    if (!adjust || locked || provisional) return;
    const next = readjustOnContext(before, verdict, options, key);
    if (!next) return;
    if (!next.custom) setComposing(false);
    // Composition perdue : proposée de nouveau là où elle redevient possible (retour au lieu ou au mois d'avant).
    if (choice) memory.current = choice;
    setNote({ text: readjustText(next.adjusted, next.cause), tone: next.custom ? "info" : "warn" });
    onChange(next.scenario, next.custom);
  }, [options, fresh, context, adjust, locked, provisional, key, onChange]);

  const write = (next: WeatherCustom, kept: ("wind" | "temperature")[] = [], viaKeys = false) => {
    if (locked) return locked();
    setNote(null);
    setAdjusted(kept);
    onChange(CUSTOM, next, viaKeys ? { deferred: true } : undefined);
  };
  const seasonal = () => {
    if (locked) return locked();
    if (choice) memory.current = choice;
    setComposing(false);
    setAdjusted([]);
    setNote(null);
    // Rien à écrire si le dossier est déjà sur la saisonnière ; un scénario importé ou une valeur illisible, si.
    if (state !== "seasonal" || (scenario !== null && scenario !== SEASONAL)) onChange(SEASONAL, null);
  };
  /** Action attendue d'un appui sur « Choisie » bloquée : celle que désigne le parent, sinon « Réessayer ». */
  const blockedCustom = () => {
    if (pending?.onBlocked) return pending.onBlocked();
    if (provisional) return useUi.getState().notify({ text: provisionalHint(), tone: "info" });
    if (query.isError) flashAttention(retryRef.current, "button");
  };
  const compose = () => {
    if (locked) return locked();
    if (blocked) return blockedCustom();
    setNote(null);
    // Options en chargement : l'intention est gardée, les tuiles viennent avec elles.
    setComposing(true);
  };
  const pickMode = (next: Mode) => {
    if (next === "seasonal") return seasonal();
    if (mode !== "custom") compose();
  };
  // Actions de l'écart (choix devenu inapplicable) : en lecture seule, elles désignent les droits.
  const gapAction = (run: () => void) => () => (readOnly ? readOnly() : run());

  const tiles = Boolean(options) && !provisional && (composing || (state === "custom" && !gap));
  // Vent et température : ceux d'un ciel proposé ici (pas d'un ciel devenu impossible).
  const shown = useLastDefined(tiles && choice && options?.sky.includes(choice.sky) ? choice : undefined);
  const hint = approximate && options
    ? approximateHint(place, options.climate)
    : (waiting || provisional) && pending?.hint
    ? pending.hint
    : provisional
      ? provisionalHint()
      : mode === "seasonal"
        ? t("La météo habituelle du climat du terrain à la date de début.")
        : tiles && !choice
          ? t("La météo de la saison reste appliquée tant qu'aucun ciel n'est choisi.")
          : !tiles && gap
            ? t("La météo enregistrée reste telle quelle tant que vous ne la changez pas.")
            : !tiles && imported
              ? t("Les ordres l'appliquent telle quelle ; vous pouvez en composer une autre.")
              : t("Seules les conditions possibles sur ce terrain à cette saison sont proposées.");
  const regional = options?.regional_wind;
  // Météo enregistrée montrée telle quelle (terrain pas prêt, écart, scénario importé) au lieu des tuiles ; pas le
  // temps du chargement (les tuiles viennent avec les options).
  const named = options ? scenarioLabel(options) : null;
  const recorded =
    !tiles && (Boolean(choice) || imported) && (Boolean(options) || waiting || provisional || query.isError)
      ? {
          icon: pictogramIcon(choice ? choice.sky : options?.preview?.pictogram),
          title: choice ? choiceLabel(choice, regional) : (named ?? t("Météo de l'ordre importé")),
          detail: choice ? t("Météo choisie") : named ? t("Météo de l'ordre importé") : undefined,
        }
      : null;

  return (
    <div data-attention={attention} className="space-y-2.5 rounded-lg">
      <div>
        <Segmented
          label={label ?? t("Météo")}
          slide
          options={[
            { value: "seasonal" as const, label: t("Saisonnière"), icon: CalendarDays },
            { value: "custom" as const, label: t("Choisie"), icon: SlidersHorizontal, blocked: blocked && mode !== "custom" },
          ]}
          value={mode}
          onChange={pickMode}
        />
        <p className="mt-1 text-meta leading-snug text-ink-muted">{hint}</p>
      </div>
      {conflict}
      <Presence show={Boolean(note)}>
        {note && (
          <Motion origin="fade">
            <Callout tone={note.tone} onClose={() => setNote(null)}>
              {note.text}
            </Callout>
          </Motion>
        )}
      </Presence>
      <Presence show={Boolean(recorded)}>
        {recorded && (
          <Motion origin="fade" className="space-y-2">
            <div className="flex min-h-8 items-center gap-2.5 rounded-lg border border-line bg-canvas px-3 py-2">
              <span className="flex shrink-0 text-ink-muted">
                <recorded.icon size={16} strokeWidth={1.75} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-ui leading-snug font-medium break-words text-ink">{recorded.title}</span>
                {recorded.detail && <span className="block text-meta leading-snug text-ink-muted">{recorded.detail}</span>}
              </span>
              {imported && !gap && !blocked && (
                <Button variant="link" onClick={compose}>
                  {t("Composer une météo")}
                </Button>
              )}
            </div>
          </Motion>
        )}
      </Presence>
      {/* Choix enregistré que les ordres n'appliquent pas tel quel (autre climat, fin de préparation, autre date) :
          rien n'est réécrit sans geste ; deux actions. */}
      <Presence show={Boolean(gap) && !blocked}>
        {gap && !blocked && (
          <Motion origin="fade">
            <Callout tone="warn">
              <p>{weatherGapText(gap)}</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <Button variant="link" onClick={gapAction(seasonal)}>
                  {t("Revenir à la saisonnière")}
                </Button>
                {!tiles && (
                  <Button variant="link" onClick={gapAction(compose)}>
                    {t("Choisir une autre météo")}
                  </Button>
                )}
              </div>
            </Callout>
          </Motion>
        )}
      </Presence>
      <Presence show={tiles}>
        {tiles && options && (
          <Motion origin="fade" className="space-y-3">
            {/* Composition quittée plus tôt, encore possible ici : reprise d'un appui. */}
            {!choice && memory.current && stillPossible(memory.current, options) && (
              <Button variant="link" onClick={() => write(memory.current!)}>
                {t("Reprendre « {meteo} »", { meteo: choiceLabel(memory.current, regional) })}
              </Button>
            )}
            <TileGroup
              label={t("Ciel et précipitations")}
              items={options.sky.map((id) => ({
                id,
                icon: pictogramIcon(id),
                label: skyLabel(id),
                // Ciel le plus proche de la saison, signalé tant qu'aucun n'est choisi (rien d'écrit).
                detail: !choice && id === seasonalSky(options) ? t("De saison") : undefined,
              }))}
              value={choice?.sky ?? null}
              onChange={(sky, viaKeys) => {
                const base = choice ?? { sky, wind: WIND_FROM_SKY, temp_min: null, temp_max: null };
                const { next, adjusted: kept } = withSky(base, sky, options);
                write(next, kept, viaKeys);
              }}
            />
            <Presence show={adjusted.length > 0}>
              {adjusted.length > 0 && (
                <Motion origin="fade">
                  <p className="-mt-1.5 flex items-start gap-1.5 text-meta leading-snug text-ink-muted" role="status">
                    <Info size={14} className="shrink-0 text-ink-muted" aria-hidden />
                    <span>
                      {adjusted.length === 2
                        ? t("Vent et température remis sur la saison : ceux choisis n'allaient pas avec ce ciel.")
                        : adjusted[0] === "wind"
                          ? t("Vent remis sur « Selon le ciel » : celui choisi n'allait pas avec ce ciel.")
                          : t("Température remise sur « Saisonnière » : celle choisie n'allait pas avec ce ciel.")}
                    </span>
                  </p>
                </Motion>
              )}
            </Presence>
            <Presence show={Boolean(choice && options.sky.includes(choice.sky))}>
              {shown && (
                <Motion origin="fade" className="space-y-3">
                  <TileGroup
                    label={t("Vent")}
                    items={windsFor(shown.sky, options).map((id) => {
                      // Force inconnue (« selon le ciel » sans ciel encore, le temps du chargement) : pas de précision.
                      const range = options.wind_ranges?.[id];
                      return {
                        id,
                        icon: windIcon(id, shown.sky),
                        label: windLabel(id, regional),
                        detail: range?.length === 2 ? span(range[0], range[1], "km/h") : undefined,
                      };
                    })}
                    value={shown.wind}
                    onChange={(wind, viaKeys) => write({ ...shown, wind }, [], viaKeys)}
                  />
                  <Temperature choice={shown} options={options} onChange={write} locked={locked} />
                </Motion>
              )}
            </Presence>
          </Motion>
        )}
      </Presence>
      {(place || mapSlug) && !waiting && !options && query.isError ? (
        <EmptyState
          variant="error"
          compact
          action={
            <Button ref={retryRef} variant="secondary" size="sm" onClick={() => void query.refetch()}>
              <RotateCw size={14} aria-hidden />
              {t("Réessayer")}
            </Button>
          }
        >
          {place ? t("La météo de ce lieu n'a pas pu être chargée.") : t("La météo de ce terrain n'a pas pu être chargée.")}
        </EmptyState>
      ) : (place || mapSlug) && !waiting && !options ? (
        <div className="flex items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2.5 text-meta text-ink-muted">
          <Spinner decorative />
          <Waiting>{t("Chargement de la météo…")}</Waiting>
        </div>
      ) : null}
      <Presence show={Boolean(options?.preview)}>
        {options?.preview && (
          <Preview
            options={options}
            busy={query.isPlaceholderData}
            context={
              imported && !gap
                ? t("Météo importée, {climate}", { climate: climateLabel(options.climate) })
                : choice && options.valid && !provisional
                  ? approximate
                    ? t("Météo choisie, {climate} (estimé)", { climate: climateLabel(options.climate) })
                    : t("Météo choisie, {climate}", { climate: climateLabel(options.climate) })
                  : provisional
                    ? t("Météo saisonnière, {climate} (provisoire)", { climate: climateLabel(options.climate) })
                    : approximate
                      ? t("Météo saisonnière, {climate} (estimé)", { climate: climateLabel(options.climate) })
                      : t("Météo saisonnière, {climate}", { climate: climateLabel(options.climate) })
            }
          />
        )}
      </Presence>
    </div>
  );
}

interface Tile {
  id: string;
  icon: LucideIcon;
  label: string;
  detail?: string;
}

/** Choix exclusif en tuiles (pictogramme, libellé, précision) : groupe radio au clavier comme un groupe natif ;
 *  aucune tuile choisie tant que `value` est nul. `viaKeys` : choix fait aux flèches (la sélection suit le focus). */
function TileGroup({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: Tile[];
  value: string | null;
  onChange: (id: string, viaKeys: boolean) => void;
}) {
  const selected = items.findIndex((item) => item.id === value);
  const enabled = items.map(() => true);
  return (
    <div>
      <span className="mb-1 block text-ui font-medium text-ink-soft">{label}</span>
      <div
        role="radiogroup"
        aria-label={label}
        className="grid grid-cols-[repeat(auto-fill,minmax(6.25rem,1fr))] gap-1.5"
        onKeyDown={(e) => onRadioGroupKeyDown(e, (i) => onChange(items[i]!.id, true))}
      >
        {items.map((item, i) => {
          const on = i === selected;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={rovingTabIndex(i, selected, enabled)}
              onClick={() => onChange(item.id, false)}
              className={clsx(
                "flex min-h-16 min-w-0 flex-col items-center justify-center gap-1 rounded-lg border px-1.5 py-2 text-center transition-colors",
                on ? "border-accent bg-accent-soft text-accent" : "border-line-strong bg-surface text-ink-soft hover:bg-hover hover:text-ink",
              )}
            >
              <Icon size={16} strokeWidth={1.75} aria-hidden />
              <span className="line-clamp-2 w-full text-meta leading-tight font-medium break-words">{item.label}</span>
              {item.detail && <span className={clsx("text-caps leading-none tabular-nums", on ? "text-accent" : "text-ink-muted")}>{item.detail}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Température : celle de la saison, ou une minimale et une maximale (plage plausible du climat rappelée). La saisie
 *  s'enregistre en quittant le champ (ou sur Entrée), jamais à chaque frappe ; un champ vide est un champ absent. */
function Temperature({
  choice,
  options,
  onChange,
  locked,
}: {
  choice: WeatherCustom;
  options: WeatherOptions;
  onChange: (next: WeatherCustom) => void;
  locked?: () => void;
}) {
  const [lo, setLo] = useState<string>("");
  const [hi, setHi] = useState<string>("");
  // « Minimale et maximale » ouverte sans valeur enregistrée (aucune paire admise à proposer) ; saisie contrôlée.
  const [editing, setEditing] = useState(false);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    setLo(choice.temp_min != null ? String(choice.temp_min) : "");
    setHi(choice.temp_max != null ? String(choice.temp_max) : "");
    setChecked(false);
    if (choice.temp_min != null) setEditing(false);
  }, [choice.temp_min, choice.temp_max]);
  const recorded = choice.temp_min != null && choice.temp_max != null;
  const fixed = recorded || editing;
  const bounds = options.temp_bounds;
  const problem = checked ? temperatureProblem(choice.sky, degrees(lo), degrees(hi), bounds) : null;
  // Température de la saison pour ce ciel (celle de l'aperçu), à défaut celle de la saison.
  const seasonal = (options.valid ? options.preview?.temp : null) ?? options.seasonal_temp ?? null;
  const commit = () => {
    setChecked(true);
    const a = degrees(lo);
    const b = degrees(hi);
    if (temperatureProblem(choice.sky, a, b, bounds) !== null) return;
    if (Math.round(a) === choice.temp_min && Math.round(b) === choice.temp_max) return;
    onChange({ ...choice, temp_min: Math.round(a), temp_max: Math.round(b) });
  };
  return (
    <div>
      <Segmented
        label={t("Température")}
        size="sm"
        slide
        options={[
          { value: "seasonal" as const, label: t("Saisonnière") },
          { value: "fixed" as const, label: t("Minimale et maximale") },
        ]}
        value={fixed ? "fixed" : "seasonal"}
        onChange={(next) => {
          if (locked) return locked();
          setChecked(false);
          if (next === "seasonal") {
            setEditing(false);
            setLo("");
            setHi("");
            if (recorded) onChange({ ...choice, temp_min: null, temp_max: null });
            return;
          }
          // Paire de la saison ramenée dans les règles du ciel ; sinon des champs vides, rien d'écrit.
          const pair = admittedTemperature(choice.sky, seasonal, bounds);
          if (!pair) {
            setEditing(true);
            return;
          }
          setLo(String(pair[0]));
          setHi(String(pair[1]));
          onChange({ ...choice, temp_min: pair[0], temp_max: pair[1] });
        }}
      />
      {!fixed && seasonal && (
        <p className="mt-1 text-meta leading-snug text-ink-muted">
          {t("Celle de la saison pour ce ciel : {range}.", { range: span(seasonal[0], seasonal[1], "°C") })}
        </p>
      )}
      <Presence show={fixed}>
        {fixed && (
          <Motion origin="fade" className="mt-2 space-y-1">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("Minimale")}>
                <Degrees value={lo} bounds={bounds} invalid={Boolean(problem)} locked={locked} onChange={setLo} onCommit={commit} />
              </Field>
              <Field label={t("Maximale")}>
                <Degrees value={hi} bounds={bounds} invalid={Boolean(problem)} locked={locked} onChange={setHi} onCommit={commit} />
              </Field>
            </div>
            <Presence show={Boolean(problem)}>
              {problem ? (
                <Motion origin="fade">
                  <p role="alert" className="text-meta leading-snug text-danger">
                    {problem}{" "}
                    {recorded
                      ? t("Température appliquée : {range}.", { range: span(choice.temp_min!, choice.temp_max!, "°C") })
                      : seasonal
                        ? t("Température appliquée : celle de la saison ({range}).", { range: span(seasonal[0], seasonal[1], "°C") })
                        : null}
                  </p>
                </Motion>
              ) : null}
            </Presence>
            {!problem && (
              <p className="text-meta leading-snug text-ink-muted">
                {t("Plage plausible ici : {range}.", { range: span(bounds[0], bounds[1], "°C") })}
              </p>
            )}
          </Motion>
        )}
      </Presence>
    </div>
  );
}

/** Champ de degrés : unité affichée dans le champ, à droite. Clavier numérique avec le signe moins (pas de
 *  `inputMode="numeric"`, qui l'ôte au doigt). Enregistré en quittant le champ ou sur Entrée. */
function Degrees({
  value,
  bounds,
  invalid,
  locked,
  onChange,
  onCommit,
}: {
  value: string;
  bounds: [number, number];
  invalid: boolean;
  locked?: () => void;
  onChange: (value: string) => void;
  onCommit: () => void;
}) {
  return (
    <div className="relative">
      <input
        type="number"
        className={clsx(inputClass, "pr-9 tabular-nums", invalid && "border-danger")}
        min={bounds[0]}
        max={bounds[1]}
        step={1}
        aria-invalid={invalid || undefined}
        readOnly={Boolean(locked)}
        onClick={locked}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => !locked && onCommit()}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !locked) {
            e.preventDefault();
            onCommit();
          }
        }}
      />
      <span aria-hidden className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-ui text-ink-muted">
        °C
      </span>
    </div>
  );
}

/** Aperçu de la météo appliquée et de l'éphéméride du jour de l'heure H ; estompé (et signalé occupé) le temps
 *  d'une relecture, sans mêler un nouveau ciel aux chiffres de l'ancien. */
function Preview({ options, context, busy }: { options: WeatherOptions; context: string; busy: boolean }) {
  const p = options.preview!;
  const Icon = pictogramIcon(p.pictogram);
  const title = pictogramLabel(p.pictogram);
  const e = options.ephemeris;
  const flight = flightEffect(p);
  // Petits drones (chantier « Drones ») : leur état à côté du vol des hélicoptères, nuit noire comprise.
  const drones = smallDronesEffect(p.small_drones, e?.darkness);
  const night = e ? darkNightLabel(e) : null;
  return (
    <Motion
      key={`${p.pictogram}-${p.assessment}`}
      origin="fade"
      role="group"
      aria-label={previewLine(options)}
      aria-busy={busy || undefined}
      data-testid="weather-preview"
      className={clsx(
        "@container rounded-lg border border-line bg-canvas px-3 py-2.5 transition-opacity duration-(--gx-enter)",
        busy && "opacity-60",
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface text-ink shadow-sm">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-ui font-semibold text-ink">{title}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={assessmentTone(p.severity)}>{assessmentLabel(p.assessment)}</Badge>
            <span className="text-meta leading-snug text-ink-muted">{context}</span>
          </div>
        </div>
      </div>
      <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 @md:grid-cols-4">
        <Metric icon={Thermometer} label={t("Température")} value={temperatureText(p.temp)} />
        <Metric icon={Eye} label={t("Visibilité")} value={visibilityText(p.visibility_km)} />
        <Metric icon={Cloud} label={t("Plafond")} value={ceilingText(p.ceiling_ft)} />
        <Metric icon={Wind} label={t("Vent")} value={windText(p.wind_kmh)} />
      </dl>
      {(options.delay_pct > 0 || flight || drones) && (
        <ul className="mt-2.5 space-y-1 border-t border-line pt-2">
          {options.delay_pct > 0 && (
            <Effect icon={Hourglass}>{t("Délais de mise en place et de mouvement majorés de {pct} % dans les ordres.", { pct: options.delay_pct })}</Effect>
          )}
          {flight && <Effect icon={Plane}>{flight}</Effect>}
          {drones && <Effect icon={Drone}>{drones}</Effect>}
        </ul>
      )}
      {e && (
        <div className="mt-2.5 border-t border-line pt-2">
          <SectionLabel as="p" inset className="mb-1">
            {t("Éphéméride du jour J : {day}", { day: new Date(`${e.day}T12:00:00`).toLocaleDateString(locale(), { day: "numeric", month: "long" }) })}
          </SectionLabel>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-meta text-ink-soft tabular-nums">
            {e.sun_state === "normal" ? (
              <>
                <Stamp icon={<Sunrise size={14} aria-hidden />} label={t("Lever du soleil")}>{e.sunrise ?? "—"}</Stamp>
                <Stamp icon={<Sunset size={14} aria-hidden />} label={t("Coucher du soleil")}>{e.sunset ?? "—"}</Stamp>
              </>
            ) : (
              <span>{e.sun_state === "up" ? t("Soleil levé toute la journée") : t("Soleil couché toute la journée")}</span>
            )}
            {e.bmnt && e.eent && <span>{t("Crépuscule nautique {bmnt} – {eent}", { bmnt: e.bmnt, eent: e.eent })}</span>}
            <span className="inline-flex items-center gap-1">
              <span className="flex shrink-0 text-ink-muted">
                <MoonPhase pct={e.moon_pct} waxing={e.moon_waxing} south={e.south} />
              </span>
              {t("Lune {pct} %", { pct: e.moon_pct })}
            </span>
            {night && <Badge size="sm">{night}</Badge>}
          </div>
        </div>
      )}
    </Motion>
  );
}

function Metric({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-meta text-ink-muted">
        <Icon size={12} aria-hidden />
        {label}
      </dt>
      <dd className="truncate text-ui font-medium text-ink tabular-nums">{value}</dd>
    </div>
  );
}

function Effect({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <li className="flex items-start gap-1.5 text-meta leading-snug text-ink-soft">
      <Icon size={14} className="shrink-0 text-ink-muted" aria-hidden />
      <span>{children}</span>
    </li>
  );
}

function Stamp({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1" title={label}>
      <span className="flex shrink-0 text-ink-muted">{icon}</span>
      <span className="sr-only">{label} </span>
      {children}
    </span>
  );
}
